/**
 * Resolve which AB line the strips were designed on when a saved trial is
 * re-opened for edit.
 *
 * On save, `selectedForStrips` is stripped from every AB line (see
 * useFormHandling), so it is never present in the reloaded payload. The only
 * authoritative record of the choice is `strip_design.selected_ab_line_index`.
 * If that index is restored to the wrong line — e.g. silently defaulting to the
 * first AB line — the strip capacity check validates against a different AB
 * line's DIRECTION and rejects strip counts that actually fit, so a design built
 * on a non-first AB line can no longer be re-applied on edit.
 *
 * Resolution order:
 *   1. `strip_design.selected_ab_line_index`, when it is a valid in-range index.
 *   2. any AB line still flagged `selectedForStrips` (legacy / defensive).
 *   3. the first AB line.
 */
export const resolveStripAbLineIndex = (
  abLines: Array<{ selectedForStrips?: boolean }> | null | undefined,
  stripDesignSelectedIndex: number | null | undefined
): number => {
  if (!Array.isArray(abLines) || abLines.length === 0) {
    return 0;
  }

  if (
    typeof stripDesignSelectedIndex === "number" &&
    Number.isInteger(stripDesignSelectedIndex) &&
    stripDesignSelectedIndex >= 0 &&
    stripDesignSelectedIndex < abLines.length
  ) {
    return stripDesignSelectedIndex;
  }

  const flaggedIndex = abLines.findIndex((line) => line?.selectedForStrips);
  return Math.max(flaggedIndex, 0);
};
